import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, Check, Search, Target, DollarSign, TrendingUp, Gift, Star, Users, Award, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Commission Tiers - Based on 30-Day DJ Course sales only (£45.99)
const COMMISSION_TIERS = [
  { name: 'Bronze', range: '0-9 sales/month', rate: 8, color: '#CD7F32' },
  { name: 'Silver', range: '10-19 sales/month', rate: 10, color: '#C0C0C0' },
  { name: 'Gold', range: '20+ sales/month', rate: 12, color: '#D4AF37' },
];

const Referral = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (user) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getCurrentTier = () => {
    if (!stats) return COMMISSION_TIERS[0];
    const sales = stats.monthly_sales || 0;
    if (sales >= 20) return COMMISSION_TIERS[2];
    if (sales >= 10) return COMMISSION_TIERS[1];
    return COMMISSION_TIERS[0];
  };

  // Guest View (Not logged in)
  if (!user) {
    return (
      <div className="min-h-screen py-24" data-testid="referral-guest-page">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="referral-title">
                Earn Money by Helping DJs <span style={{ color: '#D4AF37' }}>Level Up</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Join the Blendonics Referral Programme and earn up to £12 per sale of the 30-Day Beginner DJ Course while helping aspiring DJs achieve their dreams.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="glass-panel rounded-2xl p-8 border">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Search size={28} style={{ color: '#D4AF37' }} />
                  How It Works
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ background: '#D4AF37', color: '#141414' }}>1</div>
                    <div>
                      <p className="font-semibold">Get Your Personal Discount Code</p>
                      <p className="text-sm text-muted-foreground">Receive your own unique referral code after signing up</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ background: '#D4AF37', color: '#141414' }}>2</div>
                    <div>
                      <p className="font-semibold">Share with Your Network</p>
                      <p className="text-sm text-muted-foreground">Your followers get 10% off the 30-Day Beginner DJ Course</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ background: '#D4AF37', color: '#141414' }}>3</div>
                    <div>
                      <p className="font-semibold">Earn £8-£12 Per Course Sale</p>
                      <p className="text-sm text-muted-foreground">Higher rates based on monthly sales volume</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ background: '#D4AF37', color: '#141414' }}>4</div>
                    <div>
                      <p className="font-semibold">Get Paid Monthly</p>
                      <p className="text-sm text-muted-foreground">Payments via bank transfer on the 1st of every month (min £40)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-panel rounded-2xl p-8 border">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Gift size={28} style={{ color: '#D4AF37' }} />
                  Commission Tiers
                </h3>
                <div className="space-y-4">
                  {COMMISSION_TIERS.map((tier, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-4 rounded-lg border"
                      style={{ borderColor: tier.color, background: `${tier.color}10` }}
                    >
                      <div>
                        <p className="font-bold" style={{ color: tier.color }}>{tier.name} Tier</p>
                        <p className="text-sm text-muted-foreground">{tier.range}</p>
                      </div>
                      <span className="text-2xl font-bold" style={{ color: tier.color }}>£{tier.rate}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  * Commission applies to 30-Day Beginner DJ Course sales only, not Blendonics memberships/subscriptions.
                </p>
              </div>
            </div>

            <div className="glass-panel rounded-2xl p-8 border mb-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Star size={28} style={{ color: '#D4AF37' }} />
                Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold mb-3">What You Get:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <DollarSign size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Up to £12 commission per course sale</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Higher tiers for more sales</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Real-time tracking dashboard</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Priority access to future products</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold mb-3">What Your Followers Get:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Check size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">10% discount on the DJ Course (£45.99 → £41.39)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Lifetime access to course content</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check size={18} style={{ color: '#D4AF37' }} />
                      <span className="text-sm">Free DJ resources sent to inbox</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions Section */}
            <div className="glass-panel rounded-2xl p-8 border mb-8">
              <button
                onClick={() => setShowTerms(!showTerms)}
                className="w-full flex items-center justify-between text-left"
              >
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <FileText size={24} style={{ color: '#D4AF37' }} />
                  Referral Programme Terms & Conditions
                </h3>
                {showTerms ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
              
              {showTerms && (
                <div className="mt-6 prose prose-invert prose-sm max-w-none text-muted-foreground">
                  <p className="text-xs mb-4">Last updated: 09/01/2026</p>
                  
                  <p className="mb-4">By participating in the Blendonics Referral Programme (&quot;Programme&quot;), you (&quot;Affiliate&quot;, &quot;you&quot;, &quot;your&quot;) agree to the following Terms & Conditions set by Blendonics (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).</p>

                  <h4 className="font-bold text-white mt-6 mb-2">1. Programme Overview</h4>
                  <p>The Blendonics Referral Programme allows approved affiliates to earn commission by promoting the <strong>30-Day Beginner DJ Course</strong> using a unique personal discount/referral code. Commissions are earned when a customer completes a qualifying purchase through your referral code.</p>
                  <p className="mt-2"><strong>Important:</strong> This referral programme applies exclusively to sales of the 30-Day Beginner DJ Course (£45.99) and does NOT apply to Blendonics membership subscriptions (Starter, Pro, or Elite tiers).</p>

                  <h4 className="font-bold text-white mt-6 mb-2">2. Eligibility</h4>
                  <p>To participate, you must:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Be at least 18 years old</li>
                    <li>Provide accurate personal and banking information</li>
                    <li>Be approved by Blendonics before earning commissions</li>
                  </ul>
                  <p className="mt-2">Blendonics reserves the right to accept or reject any application at its discretion.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">3. Referral Tracking & Discount Codes</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Each affiliate will receive a unique personal discount code</li>
                    <li>Customers using your code receive 10% off the 30-Day Beginner DJ Course</li>
                    <li>Referrals are tracked via our internal systems</li>
                    <li>Blendonics is not responsible for lost commissions due to incorrect code usage or technical issues beyond reasonable control</li>
                  </ul>

                  <h4 className="font-bold text-white mt-6 mb-2">4. Commission Structure</h4>
                  <p>Affiliate commission is calculated based on the total number of eligible <strong>30-Day Beginner DJ Course</strong> sales completed within a single calendar month.</p>
                  <div className="my-4 p-4 rounded-lg border border-border">
                    <p className="font-bold mb-2">Commission Tiers:</p>
                    <ul className="space-y-2">
                      <li><span className="font-semibold" style={{ color: '#CD7F32' }}>Bronze Tier (0–9 sales per month)</span> — £8 commission per course sale</li>
                      <li><span className="font-semibold" style={{ color: '#C0C0C0' }}>Silver Tier (10–19 sales per month)</span> — £10 commission per course sale</li>
                      <li><span className="font-semibold" style={{ color: '#D4AF37' }}>Gold Tier (20+ sales per month)</span> — £12 commission per course sale</li>
                    </ul>
                  </div>
                  <p>Commission tiers are reset monthly and are based solely on the number of qualifying sales recorded during that month. Only successful, paid, and non-refunded purchases are eligible for commission. Blendonics reserves the right to review and adjust commission tiers, rates, or eligibility criteria with reasonable notice.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">5. Payouts & Minimum Payout Threshold</h4>
                  <p><strong>5.1 Payout Method</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Commissions are paid monthly via bank transfer</li>
                    <li>Payments are processed on or around the 1st of each month</li>
                  </ul>
                  
                  <p className="mt-4"><strong>5.2 Minimum Payout Threshold</strong></p>
                  <p>A minimum balance of <strong>£40</strong> must be reached before a payout is issued.</p>
                  <p className="mt-2">If the minimum payout threshold is not met within a given calendar month, the affiliate&apos;s earnings for that month will expire at the end of that month and will reset to £0 at the start of the next calendar month.</p>
                  <p className="mt-2">This policy ensures that commissions are calculated and paid on a month-by-month basis, and that monthly performance tiers (Bronze, Silver, Gold) apply only to that specific month.</p>
                  <p className="mt-2">Blendonics will always act fairly and transparently when applying this rule and will provide affiliates with access to their sales data so they can track progress toward the monthly payout threshold.</p>
                  
                  <div className="my-4 p-4 rounded-lg border border-border bg-card">
                    <p className="font-bold mb-2">Worked Example:</p>
                    <p>In June, an affiliate earns £32 in commissions. Because the total is below the £40 minimum payout threshold, the £32 expires at the end of June and the balance resets to £0 on 1 July.</p>
                    <p className="mt-2">If the affiliate earns £48 in July, the full £48 will be paid on the next scheduled payout date (around the 1st of August).</p>
                  </div>

                  <p className="mt-4"><strong>5.3 Banking Details</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Affiliates must provide accurate bank account details</li>
                    <li>Blendonics is not responsible for delays or failed payments caused by incorrect information</li>
                  </ul>

                  <h4 className="font-bold text-white mt-6 mb-2">6. Refunds, Chargebacks & Fraud</h4>
                  <p>Commissions will not be paid on refunded, cancelled, or charged-back purchases. Blendonics reserves the right to withhold or revoke commissions in cases of:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Fraudulent activity</li>
                    <li>Self-referrals</li>
                    <li>Misleading or dishonest promotion</li>
                    <li>Abuse of discount codes</li>
                  </ul>

                  <h4 className="font-bold text-white mt-6 mb-2">7. Promotion Guidelines</h4>
                  <p>Affiliates must:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Promote Blendonics honestly and ethically</li>
                    <li>Not make false income or performance claims</li>
                    <li>Not engage in spam, misleading ads, or impersonation</li>
                    <li>Comply with all applicable advertising, consumer protection, and data laws</li>
                  </ul>
                  <p className="mt-2">Blendonics may request removal of content that does not align with brand standards.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">8. Taxes</h4>
                  <p>Affiliates are solely responsible for:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Declaring income earned through the Programme</li>
                    <li>Paying any applicable taxes or fees required by law</li>
                  </ul>
                  <p className="mt-2">Blendonics does not provide tax advice.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">9. Termination</h4>
                  <p>Blendonics reserves the right to:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Suspend or terminate an affiliate account at any time</li>
                    <li>Withhold unpaid commissions if terms are breached</li>
                    <li>Modify or discontinue the Programme with reasonable notice</li>
                  </ul>
                  <p className="mt-2">Affiliates may leave the Programme at any time by written request.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">10. Programme Changes</h4>
                  <p>Blendonics may update: commission rates, payout thresholds, eligible products, and programme structure. Any changes will be communicated via email or official website updates.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">11. Limitation of Liability</h4>
                  <p>Blendonics shall not be liable for:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Indirect or consequential losses</li>
                    <li>Technical tracking issues</li>
                    <li>Delayed payments caused by third-party services</li>
                  </ul>

                  <h4 className="font-bold text-white mt-6 mb-2">12. Governing Law</h4>
                  <p>These Terms & Conditions are governed by and construed in accordance with the laws of England and Wales.</p>

                  <h4 className="font-bold text-white mt-6 mb-2">13. Contact</h4>
                  <p>For questions regarding the Referral Programme, contact:</p>
                  <p className="mt-2">📧 <a href="mailto:contact@blendonics.com" style={{ color: '#D4AF37' }}>contact@blendonics.com</a></p>
                </div>
              )}
            </div>

            <div className="text-center glass-panel rounded-2xl p-12 border-2" style={{ borderColor: '#D4AF37' }}>
              <h3 className="text-3xl font-bold mb-4">Ready to Join?</h3>
              <p className="text-muted-foreground mb-6">
                Sign up now to get your personal referral code and start earning up to £12 per course sale.
              </p>
              <Button 
                size="lg" 
                className="rounded-full transition-all duration-300"
                style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
                onClick={() => navigate('/signup')}
                data-testid="join-referral-button"
              >
                Get Started Now
              </Button>
            </div>

            <div className="mt-12 text-center">
              <p className="text-sm text-muted-foreground">
                Questions? Email us at{' '}
                <a href="mailto:contact@blendonics.com" className="hover:underline" style={{ color: '#D4AF37' }}>
                  contact@blendonics.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading referral stats...</div>
      </div>
    );
  }

  const currentTier = getCurrentTier();

  // Member Dashboard View
  return (
    <div className="min-h-screen py-24" data-testid="referral-member-page">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" data-testid="referral-dashboard-title">
            Your Referral Dashboard
          </h1>
          <p className="text-muted-foreground">
            Earn up to £12 per 30-Day DJ Course sale
          </p>
        </div>

        <div className="glass-panel rounded-2xl p-8 border mb-8">
          <h2 className="text-xl font-bold mb-4">Your Referral Code</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              value={stats?.referral_code || ''}
              readOnly
              className="text-lg font-mono font-bold"
              data-testid="referral-code-input"
            />
            <Button
              onClick={handleCopy}
              className="rounded-full transition-all duration-300"
              style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
              data-testid="copy-code-button"
            >
              {copied ? <Check size={20} className="mr-2" /> : <Copy size={20} className="mr-2" />}
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Share this code with your followers. They get 10% off the 30-Day DJ Course, and you earn commission on every sale!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="glass-panel rounded-xl p-6 border" data-testid="stat-card-sales">
            <div className="flex items-center justify-between mb-2">
              <Target size={24} style={{ color: '#D4AF37' }} />
              <span className="text-3xl font-bold font-mono">{stats?.monthly_sales || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Sales This Month</p>
          </div>

          <div className="glass-panel rounded-xl p-6 border" data-testid="stat-card-tier">
            <div className="flex items-center justify-between mb-2">
              <Award size={24} style={{ color: currentTier.color }} />
              <span className="text-xl font-bold" style={{ color: currentTier.color }}>{currentTier.name}</span>
            </div>
            <p className="text-sm text-muted-foreground">Current Tier</p>
          </div>

          <div className="glass-panel rounded-xl p-6 border" data-testid="stat-card-rate">
            <div className="flex items-center justify-between mb-2">
              <DollarSign size={24} style={{ color: '#D4AF37' }} />
              <span className="text-3xl font-bold font-mono">£{currentTier.rate}</span>
            </div>
            <p className="text-sm text-muted-foreground">Per Course Sale</p>
          </div>

          <div className="glass-panel rounded-xl p-6 border" data-testid="stat-card-earnings">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={24} style={{ color: '#D4AF37' }} />
              <span className="text-3xl font-bold font-mono">£{(stats?.monthly_earnings || 0).toFixed(2)}</span>
            </div>
            <p className="text-sm text-muted-foreground">Earnings This Month</p>
          </div>
        </div>

        {/* Payout Progress */}
        <div className="glass-panel rounded-2xl p-6 border mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">Payout Progress</h3>
            <span className="text-sm text-muted-foreground">Minimum: £40</span>
          </div>
          <div className="w-full h-4 rounded-full bg-card border overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(((stats?.monthly_earnings || 0) / 40) * 100, 100)}%`,
                background: (stats?.monthly_earnings || 0) >= 40 ? '#22c55e' : '#D4AF37'
              }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span>£{(stats?.monthly_earnings || 0).toFixed(2)} earned</span>
            <span className={(stats?.monthly_earnings || 0) >= 40 ? 'text-green-500 font-bold' : 'text-muted-foreground'}>
              {(stats?.monthly_earnings || 0) >= 40 ? '✓ Eligible for payout!' : `£${(40 - (stats?.monthly_earnings || 0)).toFixed(2)} to go`}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Earnings below £40 expire at month end. Payouts processed on the 1st of each month.
          </p>
        </div>

        <div className="glass-panel rounded-2xl p-8 border mb-8">
          <h2 className="text-2xl font-bold mb-6">Commission Tiers</h2>
          <div className="space-y-4">
            {COMMISSION_TIERS.map((tier, idx) => (
              <div 
                key={idx}
                className={`flex items-center justify-between p-4 rounded-lg border transition-all ${currentTier.name === tier.name ? 'scale-102' : 'opacity-70'}`}
                style={{ 
                  borderColor: currentTier.name === tier.name ? tier.color : '#333',
                  background: currentTier.name === tier.name ? `${tier.color}15` : 'transparent'
                }}
              >
                <div className="flex items-center gap-3">
                  {currentTier.name === tier.name && (
                    <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: tier.color }} />
                  )}
                  <div>
                    <p className="font-semibold" style={{ color: tier.color }}>{tier.name} Tier</p>
                    <p className="text-sm text-muted-foreground">{tier.range}</p>
                  </div>
                </div>
                <span className="text-2xl font-bold" style={{ color: tier.color }}>£{tier.rate}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            * Commission applies to 30-Day DJ Course sales only. Tiers reset monthly.
          </p>
        </div>

        {/* Terms & Conditions Accordion */}
        <div className="glass-panel rounded-2xl p-6 border">
          <button
            onClick={() => setShowTerms(!showTerms)}
            className="w-full flex items-center justify-between text-left"
          >
            <span className="font-bold flex items-center gap-2">
              <FileText size={20} style={{ color: '#D4AF37' }} />
              View Terms & Conditions
            </span>
            {showTerms ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showTerms && (
            <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground max-h-96 overflow-y-auto">
              <p className="mb-2"><strong>Key Points:</strong></p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Commission on 30-Day DJ Course sales only (not memberships)</li>
                <li>Bronze: £8/sale (0-9 sales), Silver: £10/sale (10-19), Gold: £12/sale (20+)</li>
                <li>Minimum £40 payout threshold - earnings below this expire monthly</li>
                <li>Payouts processed on the 1st of each month via bank transfer</li>
                <li>No commission on refunded or charged-back purchases</li>
              </ul>
              <p className="mt-4">
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/referral'); }} className="hover:underline" style={{ color: '#D4AF37' }}>
                  Read full Terms & Conditions →
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Referral;
